import './CreateDocumentLinkExecutor.scss';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ICreateDocumentLinkModel } from '@models/document/actionsExecution/ICreateDocumentLinkModel';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { useForm } from 'react-hook-form';
import { useActions } from '@hooks/useActions';
import { useTypedSelector } from '@hooks/useTypedSelector';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import DataTable, { IDataTableColumn, IDataTableHandle, parseColumnSizing } from '@srm/ui/data-table';
import { getLoadOptionsParameters } from '@utils/loadOptionsConverter';
import { useDxloSource } from '@srm/ui/data-table/dxlo';
import { ActionExecutorModel } from '@organisms/actions/ActionExecutorModel';
import { ActionTypes } from '@models/actions/ActionTypes';
import { ICreateDocumentLinkExecutorProps } from './ICreateDocumentLinkExecutorProps';
import { IForms } from '@models/Forms/IForms';
import { IDocumentData } from '@models/document/IDocumentData';
import { IField, IFieldElem } from '@models/IFormData';
import { convertListToMap, getEmptyFormDataByScheme } from '@utils/documentUtils';
import FormBuilder from '@molecules/formbuilder/FormBuilder';
import Button from '@srm/ui/button';
import { ChangeHandlersProvider } from '@controls/ChangeHandlers/ChangeHandlers';

const allowedPageSizes = [5, 10, 20];

export const CreateDocumentLinkExecutorModal: React.FC<ICreateDocumentLinkExecutorProps> = (
    props: ICreateDocumentLinkExecutorProps,
) => {
    const [errorText, setErrorText] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [okButtonDisabled, setOkButtonDisabled] = useState<boolean>();
    const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
    const [documentsModel, setDocumentsModel] = useState<ICreateDocumentLinkModel>();
    const [filterForm, setFilterForm] = useState<IForms>();
    const [filterFieldsData, setFilterFieldsData] = useState<Record<string, IFieldElem>>();
    const [appliedFormValues, setAppliedFormValues] = useState<Record<string, any> | undefined>(undefined);
    // «Применить» и с теми же значениями фильтра начинает выборку заново
    const [applyCount, setApplyCount] = useState(0);
    const [isFilterTemplateEnabled, setIsFilterTemplateEnabled] = useState<boolean>(false);
    const service = useMemo(() => new DocumentExecutionService(props.docId), [props.docId]);
    const tableRef = useRef<IDataTableHandle<Record<string, unknown>>>(null);
    const formState = useForm<any>({
        mode: 'onBlur', // "onChange"
    });
    const filterFormState = useForm<IDocumentData>({
        mode: 'onBlur',
    });

    const { setActionWarning } = useActions();
    const { document: doc } = useTypedSelector((state) => state);

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setLoading(true);
        Promise.all([
            service.getCreateDocumentLinkModel(props.actionKey),
            service.getFormBuilderScheme(props.actionKey),
        ])
            .then(([createDocumentLinkModel, formBuilderScheme]) => {
                if (ignore) return;
                setDocumentsModel(createDocumentLinkModel.data);

                const form = formBuilderScheme.data;
                const rows = form?.view?.rows?.row;
                const hasFilterTemplate = rows !== undefined && rows.length > 0;

                setIsFilterTemplateEnabled(hasFilterTemplate);
                if (hasFilterTemplate) {
                    const emptyFormData = getEmptyFormDataByScheme(rows, form.changeActions);
                    setFilterForm(form);
                    setFilterFieldsData(convertListToMap(emptyFormData.fields));
                    filterFormState.reset(emptyFormData as IDocumentData);
                } else {
                    setFilterForm(undefined);
                    setFilterFieldsData(undefined);
                    setAppliedFormValues(undefined);
                }
            })
            .catch((error) => {
                if (!ignore) setErrorText(error);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, [props.actionKey, service]);

    const buildFormValues = (fields?: IField[]) => {
        const result: Record<string, any> = {};
        fields?.forEach((field) => {
            result[field.name] = field.value;
        });
        return result;
    };

    /** Применённый фильтр — другая выборка: таблица начинает её с первой страницы и снимает выбор. */
    const onApplyFilter = () => {
        setAppliedFormValues(buildFormValues(filterFormState.getValues().fields));
        setApplyCount((count) => count + 1);
    };

    const onSubmit = () => {
        setOkButtonDisabled(true);
        return service
            .createDocumentLink(props.actionKey, selectedKeys.map(String), doc.partsVersionToAttach)
            .then((res) => {
                setOkButtonDisabled(false);

                if (res.data.warningAction) {
                    setActionWarning(res.data.warningAction);
                    return;
                }

                props.completeHandler && props.completeHandler(true);
            })
            .catch((err) => {
                const msg = Array.isArray(err) ? err[0] : err;
                setErrorText(msg);
            })
            .finally(() => setOkButtonDisabled(false));
    };

    const onSelectionChanged = useCallback((keys: string[]) => {
        setSelectedKeys(keys);
    }, []);

    const modalContainerProps: IModalContainerProps = {
        header: props.title,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: loading,
        closeDisabled: okButtonDisabled,
        // Отобранные по фильтрам и страницам документы — ввод, который жалко терять.
        hasUnsavedInput: () => selectedKeys.length > 0,
        onCancel: () => {
            props.completeHandler && props.completeHandler(false);
        },
        errorText: errorText,
        formMethods: formState,
        onSubmit: onSubmit,
        okButtonDisabled: selectedKeys.length === 0 || okButtonDisabled,
    };

    /** Клик по строке переключает её выбор — как в прежнем гриде. */
    const onRowClick = useCallback((row: Record<string, unknown>) => {
        const key = String(row.docKey);
        const current = tableRef.current?.getSelected().keys ?? [];
        tableRef.current?.selectRows(current.includes(key) ? current.filter((k) => k !== key) : [...current, key]);
    }, []);

    const source = useDxloSource<Record<string, unknown>>({
        // service пересоздаётся по props.docId
        params: [props.docId, props.actionKey, isFilterTemplateEnabled, appliedFormValues, applyCount],
        fetch: async (options, signal) => {
            // с шаблоном фильтра до «Применить» показывать нечего
            if (isFilterTemplateEnabled && appliedFormValues === undefined) return { data: [] };
            return service.getCreateDocumentLinkData(
                props.actionKey,
                getLoadOptionsParameters(options),
                isFilterTemplateEnabled ? appliedFormValues : undefined,
                signal,
            );
        },
    });

    const columns = useMemo<IDataTableColumn<Record<string, unknown>>[]>(
        () =>
            (documentsModel?.tableColumns ?? []).map((column) => ({
                field: String(column.dataField),
                header: column.title ?? '',
                dataType: column.dataType,
                ...parseColumnSizing(column.width),
                allowSorting: column.allowSorting !== false,
                allowFiltering: column.allowFiltering !== false,
                allowHeaderFiltering: false,
            })),
        [documentsModel],
    );

    return !doc.warningAction ? (
        <ModalContainer {...modalContainerProps}>
            {documentsModel ? (
                <>
                    {isFilterTemplateEnabled && filterForm && filterFieldsData ? (
                        <div className="create-document-link-filter">
                            <ChangeHandlersProvider changeActions={filterForm.changeActions}>
                                <FormBuilder
                                    fields={filterFieldsData}
                                    setError={(errors?: string[]) => {
                                        if (errors) {
                                            console.error(errors);
                                        }
                                    }}
                                    rows={filterForm.view}
                                    isEdit={false}
                                    isNew={true}
                                    formMethods={filterFormState}
                                    includedFields={[]}
                                    docId={props.docId}
                                    uniqueKey={`${props.docId}_${props.actionKey}_filter`}
                                />
                            </ChangeHandlersProvider>
                            <div className="create-document-link-filter__actions">
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => {
                                        filterFormState
                                            .handleSubmit(onApplyFilter)()
                                            .catch(() => {});
                                    }}
                                >
                                    Применить
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <></>
                    )}
                    <DataTable<Record<string, unknown>>
                        controlRef={tableRef}
                        columns={columns}
                        getRowId={(row) => String(row.docKey)}
                        source={source}
                        defaultPageSize={documentsModel?.pageSize}
                        pageSizes={allowedPageSizes}
                        showFilterRow
                        selectable
                        selectAllMode="allPages"
                        onSelectionChange={onSelectionChanged}
                        onRowClick={onRowClick}
                        emptyText="Ничего не найдено"
                    />
                </>
            ) : (
                <></>
            )}
        </ModalContainer>
    ) : (
        <ActionExecutorModel
            type={ActionTypes.Document}
            objId={doc?.data?.id?.toString() ?? '-1'}
            action={{
                displayName: doc.warningAction.message,
                key: 'warnActionKey',
                type: doc.warningAction?.type,
                hasAccessibility: false,
                hasGroupAccessibility: false,
                options: {
                    actionData: doc.warningAction?.actionData,
                    docData: doc.data,
                },
            }}
            completeHandler={() => {
                setActionWarning(undefined);
            }}
        />
    );
};
